//REACT
import React, {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// STYLES
import {
  InterestPointsMenu,
  MapContainer,

  PreloaderWrapper,
} from "./MapStyled";

// LIBRARIES
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { useLocation, useNavigate, useParams } from "react-router-dom";

//MISC
import { ButtonType } from "components/Atoms/ButtonAtom/ButtonAtomModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { ModalType } from "components/Atoms/ResponsiveModal/ResponsiveModalModel";
import { DEVICE_TYPE } from "constants/variables";
import modRewrite from "locales/modRewrite";

import {

  IpCategory,

  IpPoint,
  mapper,
} from "./MapModel";

// REDUX

// COMPONENTS
import ButtonAtom from "components/Atoms/ButtonAtom/ButtonAtom";
import CustomText from "components/Atoms/CustomText/CustomText";
import Preloader from "components/Atoms/Lotties/Preloader";
import MapDropdownAtom from "components/Atoms/MapDropDownAtom/MapDropdownAtom";
import { MapDropdownWrapper } from "components/Atoms/MapDropDownAtom/MapDropdownAtomStyled";
import ResponsiveModal from "components/Atoms/ResponsiveModal/ResponsiveModal";
import { CheckProps } from "components/Atoms/SelectInputFilter/SelectInputFilterModel";
import EventsGirdle from "components/EventsGirdle/EventsGirdle";
import newsAndMediaService from "services/newsAndMediaService";
import formatDate from "utils/formatDate";
import ShelterInfoModal from "./ShelterInfoModal";
import { useMapMarkers } from "./hooks/useMapMarkers";
import { useMapData } from "./hooks/useMapData";
import { useMapCategories } from "./hooks/useMapCategories";
import { useMapInstance } from "./hooks/useMapInstance";
import { MapDataFeedback, type MapFeedbackState } from "./MapDataFeedback";

import { useSelector } from "react-redux";
import { selectLanguage } from "slices/LanguageSlice/languageSlice";

mapboxgl.accessToken =
  "pk.eyJ1Ijoib3Jzb25hcGlrZXkiLCJhIjoiY2w4Y3Y4YXFrMTVmYzNxcGJnNnRpNXRvdCJ9.RaCSdr1r6sz6NpyL8vFwqA";

const EMPTY_EVENT_POINTS: IpPoint[] = [];
const MapComponent: React.FC = () => {
  // PROPERTIES
  const location = useLocation();
  const { rgxId } = useParams();
  let paramCategoryId = 10;
  if (rgxId !== undefined) {
    const tmpIds: any = rgxId.split("-");
    paramCategoryId = parseInt(tmpIds.pop());
  } else {
    paramCategoryId = location?.state?.id || 10;
  }

  // LIBRARY CONSTANTS
  const { t } = useTranslation();
  const language = useSelector(selectLanguage);
  const isMobile = useMediaQuery({ maxWidth: DEVICE_TYPE.mobileHeader });
  const navigate = useNavigate();

  // STATE CONSTANTS
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth();
  const years = Array.from({ length: 50 }, (_, i) => 2000 + i);
  const currentYear = new Date().getFullYear();
  const currentYearIndexDefault = years.indexOf(currentYear);
  const [year, setYear] = useState<number>(currentYearIndexDefault);
  const [month, setMonth] = useState(currentMonth);
  const [searchTerm, setSearchTerm] = useState("");
  const [checkedState, setCheckedState] = useState<CheckProps>({
    allEvents: true,
    dsuEvents: false,
    partnerEvents: false,
  });

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const [pinInfo, setPinInfo] = useState<any | null>(null);
  const [selectedIpCategory, setSelectedIpCategory] = useState<number>(paramCategoryId);
  const mapInstance = useMapInstance(mapContainerRef);
  const { map, bounds } = mapInstance;
  const categories = useMapCategories(language);
  const ipCategories = categories.categories;
  const categoryData = useMapData(selectedIpCategory, language);
  const eventScope = `${language}:${selectedIpCategory}`;
  const [eventData, setEventData] = useState<{ scope: string; points: IpPoint[]; ready: boolean; loading: boolean; error: boolean }>({
    scope: "", points: [], ready: false, loading: false, error: false,
  });
  const [eventRevision, setEventRevision] = useState(0);
  const eventFilter = useRef("month");
  const activePoints = selectedIpCategory === 11 ? eventData.scope === eventScope ? eventData.points : EMPTY_EVENT_POINTS : categoryData.points;
  const pointsReady = selectedIpCategory === 11 ? eventData.scope === eventScope && eventData.ready : categoryData.ready;
  const dataError = selectedIpCategory === 11 ? eventData.scope === eventScope && eventData.error : categoryData.error;
  const rendering = useMapMarkers({ map, points: activePoints, categoryId: selectedIpCategory, isMobile, onPinSelect: setPinInfo });
  const showLoadingOverlay = !mapInstance.error && !categories.error && !dataError && !rendering.renderError &&
    (mapInstance.loading || categories.loading || !pointsReady || rendering.isRendering);
  const hasVisiblePoints = useMemo(() => Boolean(bounds && activePoints.some((point) => point.icon &&
    Number.isFinite(point.latitudine) && Number.isFinite(point.longitudine) &&
    point.longitudine >= bounds[0] && point.longitudine <= bounds[2] &&
    point.latitudine >= bounds[1] && point.latitudine <= bounds[3]
  )), [activePoints, bounds]);
  const feedback: MapFeedbackState = showLoadingOverlay ? null
    : mapInstance.error ? "unavailable" : categories.error ? "categoryError"
    : dataError ? "error" : rendering.renderError ? "renderError" : mapInstance.tileError ? "tileError"
    : mapInstance.tilesLoading ? "tiles" : pointsReady && bounds && !hasVisiblePoints ? "empty" : null;
  const retry = () => {
    if (mapInstance.error || mapInstance.tileError) mapInstance.retry();
    if (categories.error) categories.retry();
    if (dataError) { if (selectedIpCategory === 11) setEventRevision((value) => value + 1); else categoryData.retry(); }
    if (rendering.renderError) rendering.retry();
  };
  useEffect(() => { setPinInfo(null); }, [selectedIpCategory, language]);

  // Events retain their existing explicit calendar filters; regular categories
  // and defibrillators are fetched once by useMapData.
  useEffect(() => {
    if (selectedIpCategory !== 11) return;
    let cancelled = false;
    const scope = eventScope;
    setEventData({ scope, points: [], ready: false, loading: true, error: false });
    const eventType = checkedState.dsuEvents ? "DSU" : checkedState.partnerEvents ? "partner" : "all";
    newsAndMediaService.getEvents(1, 1000, searchTerm, eventFilter.current, month, year, eventType)
      .then((response) => {
        if (cancelled) return;
        const points = response.data.map((entry) => ({
          ...entry.attributes, id: String(entry.id),
          latitudine: Number(entry.attributes.latitudine), longitudine: Number(entry.attributes.longitudine),
          icon: entry.attributes.partener !== null ? mapper[11].pastIcon : mapper[11].icon,
        }));
        setEventData({ scope, points, ready: true, loading: false, error: false });
      }).catch(() => {
        if (!cancelled) setEventData({ scope, points: [], ready: false, loading: false, error: true });
      });
    return () => { cancelled = true; };
    // Filter inputs are applied explicitly by the calendar's search button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIpCategory, language, eventRevision]);
  const fetchFilteredEvents = async () => {
    eventFilter.current = "upcoming";
    setEventRevision((value) => value + 1);
  };
  return (
    <>
      <MapContainer>
        <MapDataFeedback state={feedback} onRetry={retry} />
        <div ref={mapContainerRef} aria-busy={showLoadingOverlay || mapInstance.tilesLoading || rendering.isRendering} style={{ position: "absolute", inset: 0 }} />
        {!isMobile ? (
          <InterestPointsMenu>
            {ipCategories.map((el: IpCategory, index: number) => {
              return (
                <li key={index}>
                  <ButtonAtom
                    text={t(`components.ipCategories.${mapper[el.id]["name"]}`)}
                    type={
                      el.id === selectedIpCategory
                        ? ButtonType.MapActive
                        : ButtonType.Audio
                    }
                    leftIcon={false}
                    rightIcon={false}
                    onClick={() => {
                      setSelectedIpCategory(el.id);
                      navigate(
                        `/harta/${modRewrite(el.nume.replace(/_/g, "-"))}-${
                          el.id
                        }`
                      );
                    }}
                  />
                </li>
              );
            })}
          </InterestPointsMenu>
        ) : (
          <MapDropdownWrapper>
            <MapDropdownAtom
              options={ipCategories.map((el: any, index) => ({
                label: t(`components.ipCategories.${mapper[el.id]["name"]}`),
                value: el.id,
                key: index,
              }))}
              title={t("map.dropDown.title")}
              selected={selectedIpCategory}
              dropdownName={t(
                `components.ipCategories.${mapper[selectedIpCategory]["name"]}`
              )}
              onConfirm={(values) => {
                setSelectedIpCategory(values[0].value);
              }}
            />
          </MapDropdownWrapper>
        )}
        {pinInfo && selectedIpCategory === 5 && (
          <ShelterInfoModal
            title={pinInfo.titlu || pinInfo.title}
            address={pinInfo.adresa || [pinInfo.address, pinInfo.city].filter(Boolean).join(", ")}
            shelter={pinInfo.civil_shelter?.data?.attributes}
            latitude={pinInfo.latitudine}
            longitude={pinInfo.longitudine}
            onClose={() => setPinInfo(null)}
          />
        )}
        {pinInfo && selectedIpCategory !== 5 && (
          <ResponsiveModal
            type={
              selectedIpCategory === 10
                ? ModalType.Defib
                : selectedIpCategory === 11
                ? ModalType.Event
                : ModalType.Info
            }
            id={pinInfo.id}
            onClose={() => {
              setPinInfo(null);
            }}
            isOpen={pinInfo !== null}
            date={
              selectedIpCategory === 11 &&
              formatDate(pinInfo.start_date, pinInfo.end_date, "ro")
            }
            selectedIpCategory={selectedIpCategory}
            title={pinInfo?.titlu || pinInfo?.title}
            canCallEmergency={pinInfo?.canCallEmergency}
            photo={
              pinInfo?.defibrilator?.photo?.url ||
              pinInfo?.photo?.data?.attributes?.url
            }
            phone={pinInfo?.numar_telefon}
            adress={
              pinInfo?.adresa ||
              [pinInfo.address, pinInfo.city].filter(Boolean).join(", ")
            }
            website={pinInfo?.website}
            long={pinInfo?.longitudine}
            lat={pinInfo?.latitudine}
            schedule={pinInfo?.schedule}
            partener={pinInfo?.partener}
            stats={
              pinInfo.defibrilator &&
              Array.isArray(pinInfo?.defibrilator?.defibrilator_schedules_id)
                ? pinInfo?.defibrilator?.defibrilator_schedules_id.map(
                    (el: any) => el.available
                  )
                : null
            }
          >
            {pinInfo.defibrilator &&
              pinInfo.defibrilator?.defibrilator_schedules_id?.length > 0 &&
              pinInfo.defibrilator?.defibrilator_schedules_id.map((el: any) => (
                <Fragment key={el.id}>
                  <CustomText text={el.days + "\n"} type={TextType.H4bold} />
                  <CustomText text={el.hours} type={TextType.P1} />
                </Fragment>
              ))}
          </ResponsiveModal>
        )}
        {selectedIpCategory === 11 && (
          <EventsGirdle
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            setCheckedState={setCheckedState}
            checkedState={checkedState}
            year={year}
            setYear={setYear}
            month={month}
            setMonth={setMonth}
            fetchFilteredEvents={fetchFilteredEvents}
          />
        )}
      </MapContainer>
      {showLoadingOverlay && (
        <PreloaderWrapper role="status" aria-live="polite" aria-atomic="true">
          <Preloader width={100} height={100} />
          <p>{t(mapInstance.loading ? "map.feedback.tiles" : !pointsReady ? "map.feedback.categoryLoading" : "map.feedback.rendering")}</p>
        </PreloaderWrapper>
      )}
    </>  );
};

export default MapComponent;
